import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../config/api";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState({
    username: "",
    phoneNumber: "",
    email: "",
    address: "",
    userId: "" // Added to store user ID explicitly
  });
  const [loading, setLoading] = useState(true);
  
  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token and parse JWT to get user ID
  const getAuthToken = () => {
    try {
      const tokenData = localStorage.getItem("authToken");
      if (!tokenData) return null;
      
      // Handle the case when authToken is a string that looks like an object
      if (tokenData.startsWith('{') && tokenData.includes('"value"')) {
        try {
          // Try parsing as JSON first
          const parsedToken = JSON.parse(tokenData);
          return parsedToken.value;
        } catch (e) {
          // If that fails, try extracting the token using regex
          const valueMatch = tokenData.match(/"value":"([^"]+)"/);
          if (valueMatch && valueMatch[1]) {
            return valueMatch[1];
          }
        }
      }
      
      // If it's just a token string, return it directly
      return tokenData;
    } catch (error) {
      console.error("Error parsing auth token:", error);
      return null;
    }
  };

  // Function to extract user ID from JWT token
  const getUserIdFromToken = (token) => {
    if (!token) return null;
    
    try {
      // JWT tokens are in format: header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Decode the payload (middle part)
      const payload = JSON.parse(atob(parts[1]));
      return payload.id || payload.userId || payload.sub;
    } catch (error) {
      console.error("Error extracting user ID from token:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const token = getAuthToken();
      
      // Check if token exists before making request
      if (!token) {
        window.location.href = "/login";
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/profile`, {
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            throw new Error(errorData.message || "Failed to fetch user data");
          } else {
            throw new Error("Failed to fetch user data");
          }
        }
        
        const data = await response.json();
        
        // Extract userId from token if not provided in profile data
        if (!data.userId && !data._id) {
          const userId = getUserIdFromToken(token);
          if (userId) {
            data.userId = userId;
          }
        }
        
        setUserData({
          ...data,
          userId: data.userId || data._id || getUserIdFromToken(token) // Store user ID
        });
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to load profile data");
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const loadingToast = toast.loading("Saving changes...");

    try {
      const token = getAuthToken();
      
      if (!token) {
        toast.error("Authentication error", { id: loadingToast });
        window.location.href = "/login";
        return;
      }

      const response = await fetch(`${API_BASE_URL}/auth/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update profile");
        } else {
          throw new Error("Failed to update profile");
        }
      }

      toast.success("Profile updated successfully!", {
        id: loadingToast,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile", {
        id: loadingToast,
      });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    const currentPassword = e.target.currentPassword.value;
    const newPassword = e.target.newPassword.value;
    const confirmPassword = e.target.confirmPassword.value;
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    
    const loadingToast = toast.loading("Updating password...");
    
    try {
      const token = getAuthToken();
      
      if (!token) {
        toast.error("Authentication error", { id: loadingToast });
        window.location.href = "/login";
        return;
      }
      
      // Extract userId from token
      const userId = userData.userId || getUserIdFromToken(token);
      
      if (!userId) {
        toast.error("User ID not found. Please log in again.", { id: loadingToast });
        return;
      }
      
      // Try with explicit userId in request body
      const requestBody = {
        currentPassword,
        newPassword,
        userId: userId
      };
      
      console.log("Sending password change request with user ID:", userId);
      
      // Try both endpoints
      let response;
      let endpointUsed;
      
      try {
        // First try the users endpoint
        response = await fetch(`${API_BASE_URL}/users/change-password`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });
        endpointUsed = "users";
      } catch (error) {
        console.log("Failed with users endpoint, trying auth endpoint...");
        // If that fails, try the auth endpoint
        response = await fetch(`${API_BASE_URL}/auth/change-password`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });
        endpointUsed = "auth";
      }
      
      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          if (errorData.message === "User not found") {
            throw new Error("User not found. Trying direct user endpoint...");
          }
          throw new Error(errorData.message || "Failed to update password");
        } else {
          throw new Error(`Server error: ${response.status}`);
        }
      }
      
      toast.success("Password updated successfully!", {
        id: loadingToast,
      });
      
      // Clear form fields
      e.target.reset();
    } catch (error) {
      console.error("Error updating password:", error);
      
      // If we get "User not found", try directly with user ID
      if (error.message.includes("User not found")) {
        try {
          const token = getAuthToken();
          const userId = userData.userId || getUserIdFromToken(token);
          
          if (!userId) {
            toast.error("User ID not found. Please log in again.", { id: loadingToast });
            return;
          }
          
          // Try direct user endpoint with ID in the URL
          const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              currentPassword: e.target.currentPassword.value,
              newPassword: e.target.newPassword.value
            })
          });
          
          if (!response.ok) {
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const errorData = await response.json();
              throw new Error(errorData.message || "Failed to update password");
            } else {
              throw new Error(`Server error: ${response.status}`);
            }
          }
          
          toast.success("Password updated successfully!", {
            id: loadingToast,
          });
          
          // Clear form fields
          e.target.reset();
          return;
        } catch (directUserError) {
          console.error("Error with direct user endpoint:", directUserError);
          toast.error(directUserError.message || "Failed to update password", {
            id: loadingToast,
          });
        }
      } else {
        toast.error(error.message || "Failed to update password", {
          id: loadingToast,
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex justify-center items-center h-48 md:h-64">
        <div className="text-purple-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm mx-auto max-w-4xl w-full">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-xl text-purple-600">Edit Your Profile</h2>
        {isEditing ? (
          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              className="px-3 py-1 md:px-4 md:py-2 bg-purple-600 text-white text-sm md:text-base rounded hover:bg-purple-700"
            >
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-3 py-1 md:px-4 md:py-2 bg-gray-200 text-gray-700 text-sm md:text-base rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="text-purple-600 flex items-center gap-1"
          >
            Edit <span className="inline-block">✏️</span>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <div>
          <label className="block mb-2 text-sm md:text-base">Name</label>
          <input
            type="text"
            name="username"
            value={userData.username || ""}
            onChange={handleChange}
            className={`w-full p-2 rounded text-sm md:text-base ${
              isEditing
                ? "bg-white border border-gray-300 focus:border-purple-500 focus:outline-none"
                : "bg-gray-50"
            }`}
            readOnly={!isEditing}
          />
        </div>
        <div>
          <label className="block mb-2 text-sm md:text-base">Phone number</label>
          <input
            type="text"
            name="phoneNumber"
            value={userData.phoneNumber || ""}
            onChange={handleChange}
            className={`w-full p-2 rounded text-sm md:text-base ${
              isEditing
                ? "bg-white border border-gray-300 focus:border-purple-500 focus:outline-none"
                : "bg-gray-50"
            }`}
            readOnly={!isEditing}
          />
        </div>
        <div>
          <label className="block mb-2 text-sm md:text-base">Email</label>
          <input
            type="email"
            name="email"
            value={userData.email || ""}
            onChange={handleChange}
            className={`w-full p-2 rounded text-sm md:text-base ${
              isEditing
                ? "bg-white border border-gray-300 focus:border-purple-500 focus:outline-none"
                : "bg-gray-50"
            }`}
            readOnly={!isEditing}
          />
        </div>
        <div>
          <label className="block mb-2 text-sm md:text-base">Address</label>
          <input
            type="text"
            name="address"
            value={userData.address || ""}
            onChange={handleChange}
            className={`w-full p-2 rounded text-sm md:text-base ${
              isEditing
                ? "bg-white border border-gray-300 focus:border-purple-500 focus:outline-none"
                : "bg-gray-50"
            }`}
            readOnly={!isEditing}
          />
        </div>
      </div>

      <div className="mt-6 md:mt-8">
        <h3 className="mb-4 text-lg">Password Changes</h3>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div className="space-y-4 md:space-y-4">
            <div className="relative">
              <input
                type={showCurrentPassword ? "text" : "password"}
                name="currentPassword"
                placeholder="Current Password"
                className="w-full p-2 border-b border-gray-300 focus:outline-none focus:border-purple-500 text-sm md:text-base pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
              >
                {showCurrentPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                name="newPassword"
                placeholder="New Password"
                className="w-full p-2 border-b border-gray-300 focus:outline-none focus:border-purple-500 text-sm md:text-base pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
              >
                {showNewPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                placeholder="Confirm New Password"
                className="w-full p-2 border-b border-gray-300 focus:outline-none focus:border-purple-500 text-sm md:text-base pr-10"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 mt-2 text-sm md:text-base w-full sm:w-auto"
          >
            Update Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;